export function handleError(error: unknown): string {
  if ( typeof error === 'object' && error !== null && 'status' in error){
    if(typeof error.status === 'string'){
      switch(error.status){
        case 'FETCH_ERROR':
        case 'TIMEOUT_ERROR':
          return 'Nuestros servidores no se encuentran disponibles en este momento. Intente nuevamente más tarde.';
        case "PARSING_ERROR":
          if('originalStatus' in error){
            if(error.originalStatus === 404){
              return 'El recurso no fue encontrado.'
            }
          }
          return 'Error al procesar la respuesta del servidor. El recurso no'
      }
    } else if (typeof error.status === 'number'){
      switch(error.status){
        case 400:
          return 'Hubo un error con la solicitud. Verifica los datos ingresados.';
        case 401:
          return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
        case 403:
          return 'No tienes permisos para realizar esta acción.';
        case 404:
          if('data' in error && typeof error.data === 'object' && error.data){
            // eslint-disable-next-line
            const data = error.data as any;

            if (data.error) {
              return data.error
            }

            return 'El recurso solicitado no fue encontrado.';

          } else{
            return 'El recurso solicitado no fue encontrado.';
          }
        case 409:
          if ('data' in error && typeof error.data === 'object' && error.data) {
            // eslint-disable-next-line
            const data = error.data as any;
            if ('message' in data) {
              return data.message;
            }
          }
          return 'No se puede eliminar el recurso porque tiene datos asociados.';
        case 500:
          if('data' in error && typeof error.data === 'object' && error.data){
            // eslint-disable-next-line
            const data = error.data as any;
            if( 'message' in data && typeof data.message === 'string'){
                return data.message.toString();
            } else{
              return 'Ha ocurrido un error en nuestro servidor. Intente nuevamente más tarde o comuníquese con Soporte.';
            }
          } else{
            return 'Ha ocurrido un error en nuestro servidor. Intente nuevamente más tarde o comuníquese con Soporte.';
          } 
        default:
          return 'Ha ocurrido un error. Intente de nuevo más tarde.'
      }
    }
  }
  return 'Ha ocurrido un error inesperado. Intente nuevamente más tarde.';
}


function normalizeError(error: unknown) {
  // Caso 1: error RTK Query con status
  if (typeof error === "object" && error !== null && "status" in error) {
    // eslint-disable-next-line
    const err = error as any;

    // 1A - Errores especiales tipo FETCH_ERROR, TIMEOUT_ERROR, etc
    if (typeof err.status === "string") {
      return {
        status: err.status,
        message: "Network error",
      };
    }

    // 1B - HTTP Error (numérico)
    if (typeof err.status === "number") {
      return {
        status: err.status,
        message:
          err.data?.message ??
          err.data?.error ??
          "Error desconocido del servidor",
        errors: err.data?.errors ?? null,
        details: err.data?.details ?? null,
      };
    }
  }

  // Caso 2: SerializedError o error inesperado
  return {
    status: "UNKNOWN",
    // eslint-disable-next-line
    message: (error as any)?.message ?? "Error inesperado",
  };
}


export function handleErrorStandard(error: unknown): string {
  const e = normalizeError(error);

  // === NETWORK ERRORS ===
  if (typeof e.status === "string") {
    switch (e.status) {
      case "FETCH_ERROR":
      case "TIMEOUT_ERROR":
        return "Nuestros servidores no están disponibles en este momento. Intenta más tarde.";
      case "PARSING_ERROR":
        return "Hubo un problema interpretando la respuesta del servidor.";
      default:
        return "Ocurrió un error de conexión.";
    }
  }

  // === HTTP ERRORS ===
  switch (e.status) {
    case 400:
      // Si tu API manda errors[]
      if (e.errors && Array.isArray(e.errors)) {
        return e.errors[0]?.msg ?? e.message;
      }
      if (e.details && typeof e.details === "object") {
        const firstKey = Object.keys(e.details)[0];
        const firstMessage = firstKey ? e.details[firstKey]?.[0] : null;
        if (firstMessage) return firstMessage;
      }
      return e.message;

    case 401:
      return "Tu sesión ha expirado. Inicia sesión nuevamente.";

    case 403:
      return e.message ?? "No tienes permisos para realizar esta acción.";

    case 404:
      return e.message ?? "El recurso no fue encontrado.";

    case 409:
      return e.message ?? "El recurso no puede ser eliminado.";

    case 422:
      if (e.errors && Array.isArray(e.errors)) {
        return e.errors[0]?.msg ?? e.message;
      }
      return e.message ?? "La operación no puede ser procesada.";

    case 500:
      return e.message ?? "Error interno del servidor.";

    default:
      return e.message ?? "Ha ocurrido un error inesperado.";
  }
}



export function generateRandomPassword() {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const symbols = "!@#$%^&*()-_=+";

  const all = uppercase + lowercase + numbers + symbols;

  const getRandom = (chars: string) =>
    chars[Math.floor(Math.random() * chars.length)];

  // Garantías estructurales
  const password = [
    getRandom(uppercase),
    getRandom(symbols),
    ...Array(6)
      .fill(null)
      .map(() => getRandom(all))
  ];

  // Mezclar
  return password.sort(() => Math.random() - 0.5).join("");
}


export function formatARS(value: string | number | null | undefined) {
  if (value === null || value === undefined) return "—";

  const numberValue =
    typeof value === "string" ? Number(value) : value;

  if (Number.isNaN(numberValue)) return "—";

  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberValue);
}


export function parseDDMMYYYY(value: string): Date | null {
  // eslint-disable-next-line no-useless-escape
  const m = value.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  if (!m) return null;

  const day = Number(m[1]);
  const month = Number(m[2]); // 1..12
  const year = Number(m[3]);

  // Creamos en horario local
  const d = new Date(year, month - 1, day);

  // Validación
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) return null;

  return d;
}
